"use client";

import { useEffect, useState } from "react";
import { Mail, Check, TriangleAlert } from "lucide-react";
import { apiClient, isPaidRequiredError, type AlertDoc } from "@/lib/api-client";
import {
  IcyDialogShell,
  PrimaryDialogButton,
  SecondaryDialogButton,
  getDialogInputClass,
  DIALOG_LABEL_CLASS,
  DIALOG_ERROR_CLASS,
} from "./IcyDialogShell";

/**
 * EditEmailDialog — changes the notification_email the alert delivers to.
 *
 * Client-side validation mirrors the server-side _EMAIL_RE in alerts.py. We
 * still rely on the server for the authoritative check; the client validation
 * only exists to give instant feedback before a round-trip.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EditEmailDialog({
  open,
  onOpenChange,
  alert,
  onSaved,
  onPaidRequired,
  viewMode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alert: AlertDoc;
  onSaved: () => void;
  onPaidRequired?: () => void;
  canMutate?: boolean;
  viewMode?: string;
}) {
  const [email, setEmail] = useState(alert.notification_email ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail(alert.notification_email ?? "");
      setError(null);
    }
  }, [open, alert.notification_email]);

  const dirty = email.trim().toLowerCase() !== (alert.notification_email ?? "").trim().toLowerCase();
  const invalid = email.trim().length > 0 && !EMAIL_RE.test(email.trim());

  const handleSave = async () => {
    if (!dirty || invalid || email.trim().length === 0) return;
    const newEmail = email.trim().toLowerCase();
    console.info("[EditEmailDialog] save clicked", {
      alertId: alert._id,
      newEmail,
    });
    setSaving(true);
    setError(null);
    try {
      await apiClient.updateAlertEmail(alert._id, newEmail);
      console.info("[EditEmailDialog] save ok", { alertId: alert._id, newEmail });
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      console.error("[EditEmailDialog] save failed", { alertId: alert._id, err });
      if (isPaidRequiredError(err)) {
        onOpenChange(false);
        onPaidRequired?.();
        return;
      }
      setError(err?.message || "Could not update email");
    } finally {
      setSaving(false);
    }
  };

  return (
    <IcyDialogShell viewMode={viewMode}
      open={open}
      onOpenChange={onOpenChange}
      title="Change notification email"
      subtitle="Where this alert sends when it fires"
      Icon={Mail}
      footer={
        <>
          <SecondaryDialogButton
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </SecondaryDialogButton>
          <PrimaryDialogButton variant={viewMode === "signal" ? "emerald" : "icy"}
            onClick={handleSave}
            busy={saving}
            disabled={!dirty || invalid || saving || email.trim().length === 0}
            Icon={Check}
          >
            Save email
          </PrimaryDialogButton>
        </>
      }
    >
      <div>
        <label className={DIALOG_LABEL_CLASS} htmlFor="alert-email">
          Email address
        </label>
        <input
          id="alert-email"
          type="email"
          className={getDialogInputClass(viewMode)}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSave();
            }
          }}
        />
        {invalid && (
          <div className={DIALOG_ERROR_CLASS}>
            <TriangleAlert className="w-3.5 h-3.5" />
            <span>Enter a valid email address.</span>
          </div>
        )}
      </div>

      <p className="text-xs text-slate-300/80 leading-relaxed">
        Emails arrive instantly when the alert fires. We use the same sender as
        every other Xynth notification, so add it to your contacts to avoid
        spam filters.
      </p>

      {error && (
        <div className={DIALOG_ERROR_CLASS}>
          <TriangleAlert className="w-3.5 h-3.5" />
          <span>{error}</span>
        </div>
      )}
    </IcyDialogShell>
  );
}
